import React from 'react';
import { Artifact } from '../../../types/artifact';
import { useTheme } from '../../../contexts/ThemeContext';

interface CodeArtifactViewerProps {
  artifact: Artifact;
  mode?: 'default' | 'editor' | 'preview';
}

export const CodeArtifactViewer: React.FC<CodeArtifactViewerProps> = ({ 
  artifact, 
  mode = 'default' 
}) => {
  const { darkMode } = useTheme();

  const getLanguageFromFilename = (filename: string): string => {
    const extension = filename.split('.').pop()?.toLowerCase() || '';
    const languageMap: Record<string, string> = {
      'js': 'javascript',
      'ts': 'typescript',
      'jsx': 'javascript',
      'tsx': 'typescript',
      'py': 'python',
      'rb': 'ruby',
      'java': 'java',
      'c': 'c',
      'cpp': 'cpp',
      'cs': 'csharp',
      'go': 'go',
      'rs': 'rust',
      'php': 'php',
      'html': 'html',
      'css': 'css',
      'json': 'json',
      'md': 'markdown',
      'sql': 'sql',
      'sh': 'bash',
      'yaml': 'yaml',
      'yml': 'yaml',
    };
    
    return languageMap[extension] || 'plaintext';
  };
  
  const getLanguage = (): string => {
    if (artifact.metadata?.language) {
      return artifact.metadata.language;
    }
    
    if (artifact.metadata?.filename) {
      return getLanguageFromFilename(artifact.metadata.filename);
    }
    
    return 'plaintext';
  };
  
  const getCodeContent = (): string => {
    if (typeof artifact.content === 'string') {
      return artifact.content;
    }
    
    if (typeof artifact.content === 'object') {
      if (artifact.content.code) {
        return artifact.content.code;
      }
      
      return JSON.stringify(artifact.content, null, 2);
    }
    
    return '';
  };
  
  const renderEditor = () => (
    <div className={`border rounded-md ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
      <div className={`px-4 py-2 border-b flex justify-between items-center ${
        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'
      }`}>
        <span className={`font-mono text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          {artifact.metadata?.filename || `code.${getLanguage()}`}
        </span>
        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {getLanguage()}
        </span>
      </div>
      <pre className={`p-4 overflow-auto text-sm font-mono ${
        darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-800'
      }`}>
        <code>{getCodeContent()}</code>
      </pre>
    </div>
  );

  const renderPreview = () => (
    <div className={`border rounded-md p-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
      <p className={`italic mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Preview not available for this code type
      </p>
      {renderEditor()}
    </div>
  );
  
  switch (mode) {
    case 'editor':
      return renderEditor();
    case 'preview':
      return renderPreview();
    default:
      return renderEditor();
  }
};
